/* FrameDex — predictive-search.js
   Accessible predictive search using Shopify's /search/suggest endpoint.
   Combobox pattern with keyboard support. Degrades to a normal search form. */
(function () {
  'use strict';

  const cache = new Map();

  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[data-predictive-results]');
      this.form = this.querySelector('form');
      this.status = this.querySelector('[data-predictive-status]');
      if (!this.input || !this.results) return;

      this.input.setAttribute('role', 'combobox');
      this.input.setAttribute('aria-expanded', 'false');
      this.input.setAttribute('aria-autocomplete', 'list');
      this.input.setAttribute('autocomplete', 'off');

      this.debounced = this.debounce(() => this.search(), 250);
      this.input.addEventListener('input', () => this.debounced());
      this.input.addEventListener('focus', () => { if (this.input.value.length >= 2) this.search(); });
      this.input.addEventListener('keydown', (e) => this.onKeydown(e));
      document.addEventListener('click', (e) => { if (!this.contains(e.target)) this.close(); });
    }

    debounce(fn, wait) {
      let t;
      return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), wait); };
    }

    async search() {
      const q = this.input.value.trim();
      if (q.length < 2) { this.close(); return; }
      this.input.setAttribute('aria-busy', 'true');
      try {
        let html;
        if (cache.has(q)) {
          html = cache.get(q);
        } else {
          const params = new URLSearchParams({
            q,
            'resources[type]': 'product,collection,page,article',
            'resources[limit]': '6',
            'section_id': 'predictive-search',
          });
          const res = await fetch(`/search/suggest?${params}`);
          const text = await res.text();
          const doc = new DOMParser().parseFromString(text, 'text/html');
          const inner = doc.querySelector('[data-predictive-inner]');
          html = inner ? inner.innerHTML : '';
          cache.set(q, html);
        }
        this.results.innerHTML = html;
        this.open();
        this.announce();
      } catch (e) {
        this.close();
      } finally {
        this.input.removeAttribute('aria-busy');
      }
    }

    announce() {
      if (!this.status) return;
      const count = this.results.querySelectorAll('[role="option"]').length;
      this.status.textContent = count
        ? `${count} Vorschläge verfügbar.`
        : 'Keine Vorschläge gefunden.';
    }

    open() {
      this.results.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
    }

    close() {
      this.results.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      this.activeIndex = -1;
    }

    get options() {
      return Array.from(this.results.querySelectorAll('[role="option"]'));
    }

    onKeydown(e) {
      const opts = this.options;
      if (e.key === 'Escape') { this.close(); return; }
      if (!opts.length) return;
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        this.activeIndex = this.activeIndex == null ? -1 : this.activeIndex;
        this.activeIndex += e.key === 'ArrowDown' ? 1 : -1;
        if (this.activeIndex < 0) this.activeIndex = opts.length - 1;
        if (this.activeIndex >= opts.length) this.activeIndex = 0;
        opts.forEach((o, i) => o.setAttribute('aria-selected', String(i === this.activeIndex)));
        const active = opts[this.activeIndex];
        active.scrollIntoView({ block: 'nearest' });
        this.input.setAttribute('aria-activedescendant', active.id || '');
      } else if (e.key === 'Enter' && this.activeIndex >= 0 && opts[this.activeIndex]) {
        const link = opts[this.activeIndex].querySelector('a') || opts[this.activeIndex];
        if (link.href) { e.preventDefault(); window.location.href = link.href; }
      }
    }
  }
  if (!customElements.get('predictive-search')) customElements.define('predictive-search', PredictiveSearch);
})();
